/**
 * @file PageLayout.tsx
 * @description Main page layout component that provides the overall structure for Sitecore pages.
 * Handles server-side rendering, Algolia search integration, and placeholder rendering for
 * various page sections (header, main, footer).
 */

// Global
import { Placeholder, RouteData, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import AlgoliaSearch from 'components/helpers/AlgoliaSearch/AlgoliaSearch';
import { AlgoliaSearchProps } from 'components/helpers/AlgoliaSearch/SearchTypes';
import BackToTopButton from 'components/helpers/BackToTop/BackToTop';
import HeadMeta from 'components/helpers/Head/HeadMeta';
import HeadNRCFonts from 'components/helpers/Head/HeadNRCFonts/HeadNRCFonts';
import SCcdp from 'components/helpers/Head/SitecoreCDP';
import SitecoreKeepAlive from 'components/helpers/SitecoreKeepAlive';
import { IsNRCTheme } from 'lib/util/site-configuration';
import { InstantSearchServerState, InstantSearchSSRProvider } from 'react-instantsearch-hooks-web';

/**
 * Props for the SearchWrapper component
 * @interface SearchWrapperProps
 * @extends {AlgoliaSearchProps}
 * @property {boolean} pageEditing - Indicates if the page is currently in Sitecore editing mode
 * @property {boolean} isSearchRobot - Indicates if the request is from a search engine crawler
 */
interface SearchWrapperProps extends AlgoliaSearchProps {
  pageEditing: boolean;
  isSearchRobot: boolean;
}

/**
 * Props for the ServerSideSearchWrapper component
 * @interface ServerSideSearchWrapperProps
 * @property {React.ReactNode | React.ReactNode[]} children - Child components to render within the wrapper
 * @property {InstantSearchServerState} [algoliaServerState] - Server-side state for Algolia InstantSearch
 * @property {boolean} isSearchRobot - Indicates if the request is from a search engine crawler
 */
interface ServerSideSearchWrapperProps {
  children: React.ReactNode | React.ReactNode[];
  algoliaServerState?: InstantSearchServerState;
  isSearchRobot: boolean;
}

/**
 * Server-side wrapper component for Algolia InstantSearch
 *
 * This component conditionally applies server-side rendering (SSR) for Algolia search.
 * On the client-side, it simply passes through children. On the server-side, it wraps
 * children with InstantSearchSSRProvider to enable proper SSR hydration.
 *
 * @param {ServerSideSearchWrapperProps} props - Component props
 * @param {React.ReactNode | React.ReactNode[]} props.children - Child components to render
 * @param {InstantSearchServerState} [props.algoliaServerState] - Server-side search state from Algolia
 * @param {boolean} props.isSearchRobot - Whether the request is from a search crawler
 * @returns {JSX.Element} Either children directly (client-side) or wrapped in InstantSearchSSRProvider (server-side)
 */
const ServerSideSearchWrapper = ({
  children,
  algoliaServerState,
}: ServerSideSearchWrapperProps): JSX.Element => {
  // If we are client-side, then skip this component.
  // The window object is only available in browser environments
  if (typeof window !== `undefined`) {
    return <>{children}</>;
  }

  // If we made it this far we are running server-side on a site that requires authentication...
  // Let InstantSearchSSR render for us with the pre-fetched server state
  return <InstantSearchSSRProvider {...algoliaServerState}>{children}</InstantSearchSSRProvider>;
};

/**
 * Wrapper component that conditionally enables Algolia search functionality
 *
 * This component determines whether to enable search features based on the editing state
 * and wraps children with the appropriate search providers. When in page editing mode,
 * search functionality is disabled to prevent interference with the Sitecore editor.
 *
 * @param {SearchWrapperProps} props - Component props
 * @param {boolean} props.pageEditing - Whether the page is in Sitecore editing mode
 * @param {React.ReactNode | React.ReactNode[]} props.children - Page content to render
 * @param {boolean} props.isSearchRobot - Whether the request is from a search crawler
 * @param {InstantSearchServerState} [props.algoliaServerState] - Server-side search state
 * @param {boolean} [props.ifRouting] - Whether routing functionality should be enabled for search
 * @param {string} [props.searchUrl] - URL of the search page
 * @returns {JSX.Element} Children wrapped with search providers or directly rendered in editing mode
 */
const SearchWrapper = ({
  pageEditing,
  children,
  isSearchRobot,
  algoliaServerState,
  ifRouting,
  searchUrl,
}: SearchWrapperProps): JSX.Element => {
  // In editing mode, bypass all search wrappers to avoid conflicts with Sitecore Experience Editor
  if (pageEditing === true) {
    return <>{children}</>;
  }

  // In normal viewing mode, wrap content with search providers
  // The nested structure ensures proper SSR hydration and search functionality
  return (
    <div id="inPageLayout">
      <ServerSideSearchWrapper
        isSearchRobot={isSearchRobot}
        algoliaServerState={algoliaServerState}
      >
        <div id="inSearchWrapper">
          <AlgoliaSearch ifRouting={ifRouting} searchUrl={searchUrl}>
            <div id="inAlgoliaSearch">{children}</div>
          </AlgoliaSearch>
        </div>
      </ServerSideSearchWrapper>
    </div>
  );
};

/**
 * Main page layout component for Sitecore JSS pages
 *
 * This component provides the complete page structure including header, main content,
 * and footer sections. It integrates with Sitecore's placeholder system to enable
 * dynamic component rendering and supports Algolia search functionality.
 *
 * Key responsibilities:
 * - Renders meta tags and theme-specific fonts in the document head
 * - Manages search integration with server-side rendering support
 * - Provides semantic HTML structure with header, main, and footer elements
 * - Applies site-specific CSS classes for multi-site theming
 * - Renders Sitecore placeholders for dynamic content areas
 *
 * @param {Object} props - Component props
 * @param {InstantSearchServerState} [props.algoliaServerState] - Server-side state for Algolia search hydration
 * @param {boolean} props.isSearchRobot - Indicates if the request is from a search engine crawler
 * @returns {JSX.Element} Complete page layout with all sections and search integration
 */
const PageLayout = ({
  algoliaServerState,
  isSearchRobot,
}: {
  algoliaServerState?: InstantSearchServerState;
  isSearchRobot: boolean;
}): JSX.Element => {
  // Get Sitecore context data including route information and editing state
  const { sitecoreContext } = useSitecoreContext();

  // Determine if we're in page editing mode (Sitecore Experience Editor)
  const pageEditing = sitecoreContext.pageEditing === true;

  // Extract route data for passing to Placeholder components
  const route = sitecoreContext.route as RouteData;

  // Determine if current page should have search functionality enabled
  // This is true if either:
  // 1. Current item path matches the configured search page URL, or
  // 2. The page explicitly has the hasSearch flag set
  const isSearchPage =
    sitecoreContext.itemPath === sitecoreContext.searchPageUrl ||
    sitecoreContext.hasSearch === true;

  // Get site-specific CSS class for multi-site theming support
  // This allows different visual themes per Sitecore site
  const siteSpecificClass = sitecoreContext.siteSpecificClass?.trim();

  return (
    <>
      {/* Document head elements - rendered outside main layout */}
      <HeadMeta />
      {/* Conditionally load NRC-specific fonts if using NRC theme */}
      {IsNRCTheme(sitecoreContext) && <HeadNRCFonts />}

      {/* Main content wrapped with search providers */}
      <SearchWrapper
        isSearchRobot={isSearchRobot}
        algoliaServerState={algoliaServerState}
        ifRouting={isSearchPage}
        searchUrl={sitecoreContext.searchPageUrl}
        pageEditing={pageEditing}
      >
        {/* Analytics and monitoring placeholders */}
        <Placeholder name="nmdp-gtm" rendering={route} />
        <Placeholder name="siteimprove" rendering={route} />

        {/* Header section with site-specific styling */}
        <header className={siteSpecificClass}>
          <Placeholder name="nmdp-banner" rendering={route} />
          {/* Sitecore CDP (Customer Data Platform) tracking */}
          <SCcdp />
          <Placeholder name="nmdp-header" rendering={route} />
          <Placeholder name="nmdp-breadcrumb" rendering={route} />
        </header>

        {/* Main content area with site-specific styling */}
        <main className={siteSpecificClass}>
          <Placeholder name="nmdp-hero" rendering={route} />
          <Placeholder name="nmdp-main" rendering={route} />
          {/* Accessibility feature: button to scroll back to top of page */}
          <BackToTopButton />
        </main>

        {/* Footer section with site-specific styling */}
        <footer className={siteSpecificClass}>
          <Placeholder name="nmdp-footer" rendering={route} />
        </footer>

        {/* Keep Sitecore session alive to prevent timeout during editing */}
        <SitecoreKeepAlive />
      </SearchWrapper>
    </>
  );
};

export default PageLayout;
